import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
  ReactElement,
  forwardRef,
  useReducer,
  ReactNode,
} from "react";
import { useCombinedRefs } from "../utils/useCombinedRefs";
import PanelMenu from "../panel-menu";
import classNames from "classnames";
import useResizeObserver from "../utils/useResizeObserver";
import { HeaderProps } from "./header.types";
import { Navigation, NavigationItem } from "./headerNavigation";
import { FocusTrap } from "../utils/FocusTrap";
import {
  HeaderCssOverrides,
  HeaderSecondaryNavCssOverrides,
} from "./css-overrides";
import { navItemsParser } from "../panel-menu/navParser";
import { NavContext, initialState } from "../panel-menu/navContext";
import { reducer } from "../panel-menu/navReducer";
import { NavTreeActionType } from "../panel-menu/navReducer.types";

export const Header = forwardRef(
  (
    {
      title,
      children,
      className,
      userBlock,
      secondaryNav,
      condensed,
      noLogo,
      customLogo,
      fixed,
      titleLink = "/",
      titleProps = {},
      moreNavAriaLabel = "More Nav",
      navAriaLabel = "Header",
      data,
      dataSecondary,
      breakpoints: customBreakpoints,
      closeOnClick: closeOnClickGlobal,
    }: HeaderProps,
    headerExternalRef
  ): ReactElement => {
    const [state, dispatch] = useReducer(reducer, initialState);

    const panelMenuRef = useRef<HTMLUListElement>(null);
    const panelSecondaryMenuRef = useRef<HTMLUListElement>(null);
    const menuOpenNode = useRef<HTMLButtonElement>(null);
    const menuCloseNode = useRef<HTMLButtonElement>(null);
    const headerNode = useRef<HTMLElement>(null);
    const menuContainerRef = useRef<HTMLDivElement>(null);
    useCombinedRefs(headerNode, headerExternalRef);
    const { width: menuContainerWidth } = useResizeObserver({
      ref: menuContainerRef as React.RefObject<HTMLDivElement>,
    });
    const [isHamburgerMenuOpen, setIsHamburgerMenuOpen] = useState(false);

    useResizeObserver({
      ref: headerNode as React.RefObject<Element>,
      onResize: ({ width: headerWidth }) => {
        dispatch({
          type: NavTreeActionType.SET_HEADER_META_DATA,
          headerWidth,
          customBreakpoints,
        });
      },
    });

    const sidebarClickEventHandler = (event: MouseEvent): void => {
      if (event.target === document.querySelector(".spark-header__nav")) {
        setIsHamburgerMenuOpen(false);
      }
    };

    useLayoutEffect(() => {
      if (isHamburgerMenuOpen) {
        document.addEventListener("mousedown", sidebarClickEventHandler);
      }

      return (): void => {
        document.removeEventListener("mousedown", sidebarClickEventHandler);
      };
    }, [isHamburgerMenuOpen]);

    const handleCloseOnClick = (
      menuItemRef: React.RefObject<HTMLLIElement>,
      isTopLevelItem?: boolean
    ): void => {
      const hideClickedMenu = (): void => {
        /**
         * Force blur on the activeElement after closing the menu
         * (blur for elements should work automatically, but due of
         * some loading mechanisms in end-use apps, it could be disrupted)
         * Task url for further investigation:
         * https://tasks.office.com/SabreNOW.onmicrosoft.com/Home/Task/-9cXgt4-00OjdYI-8-z93mQAEJje?Type=TaskLink&Channel=Link&CreatedTime=637884939297790000
         */
        (document.activeElement as HTMLElement).blur();

        const closestTopLevel = menuItemRef.current?.closest(".top-level-item");

        if (closestTopLevel) {
          // Trigger menu hiding phase using css
          closestTopLevel.setAttribute("id", "clicked-child");
        }

        setTimeout(() => {
          // Finish menu hiding phase
          closestTopLevel?.removeAttribute("id");
        }, 500);
      };

      if (!isTopLevelItem) {
        // Hide Header menu if sub item clicked
        hideClickedMenu();
      }

      // Hide hamburger menu
      setIsHamburgerMenuOpen(false);
    };

    // set focus on toogle button
    useEffect(() => {
      isHamburgerMenuOpen === true
        ? menuCloseNode.current?.focus()
        : menuOpenNode.current?.focus();
    }, [isHamburgerMenuOpen]);

    const headerClasses = classNames(
      "spark-header",
      "spark-header--overflow-checked",
      { "spark-header--fixed": fixed },
      { "spark-header--visible": !state.headerMetaData?.isSmallScreen },
      { "spark-header--collapsed": state.headerMetaData?.isSmallScreen },
      { "spark-header--condensed": condensed },
      { "spark-header--no-logo": noLogo },
      className
    );

    const navClasses = classNames("spark-header__nav", {
      active: isHamburgerMenuOpen,
    });

    /**
     * Split children into two categories: "navigation" and "other".
     * Needed for fixed panel, that interacts with the headline as a child.
     */
    const childrenCategory = useMemo((): {
      navigation: React.ReactNode[] | null;
      other: React.ReactNode[] | null;
    } => {
      const childrenArray = React.Children.toArray(children);

      const navigationChildren = childrenArray.filter(
        (child) =>
          React.isValidElement(child) &&
          (child?.type as any).displayName === "Navigation"
      );

      const otherChildren = childrenArray.filter(
        (child) =>
          React.isValidElement(child) &&
          (child?.type as any).displayName !== "Navigation"
      );

      return {
        navigation: navigationChildren?.length ? navigationChildren : null,
        other: otherChildren?.length ? otherChildren : null,
      };
    }, [children]);

    const getNavItems = (children: ReactNode): React.ReactNode[] => {
      if (React.isValidElement(children)) {
        const childrenProp = (children.props as any)?.children;
        if (childrenProp && React.Children.toArray(childrenProp).length > 0) {
          return React.Children.toArray(childrenProp);
        }
      }

      return [];
    };

    const closeOnClickHeaderGlobals = {
      isGlobal: closeOnClickGlobal,
      callback: handleCloseOnClick,
    };

    const headerNavTree = navItemsParser(
      data ? null : getNavItems(childrenCategory.navigation?.[0]),
      data ?? null,
      closeOnClickHeaderGlobals,
      true
    );

    const headerNavTreeSecondary = navItemsParser(
      dataSecondary ? null : getNavItems(secondaryNav),
      dataSecondary ?? null,
      closeOnClickHeaderGlobals,
      true,
      true
    );

    const navTreeJsonString = JSON.stringify(headerNavTree);
    const navTreeSecondaryJsonString = JSON.stringify(headerNavTreeSecondary);

    /**
     * Replace primary nav tree schema and restart measurement on items change (count or props)
     */
    useEffect(() => {
      dispatch({
        type: NavTreeActionType.REPLACE_STATE,
        changedTree: headerNavTree,
      });
      dispatch({
        type: NavTreeActionType.CLEAR_AND_START_MEASUREMENT,
      });
      /**
       * stringify version of tree is needed for comparision in dependency aray
       * because `onClick` function is breaking object comparison on every re-render
       * (callback reference changes, which gives always `true`).
       * useDeepCompareEffect also don't work here.
       */
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navTreeJsonString]);

    /**
     * Replace secondary nav tree schema on items change (count or props)
     */
    useEffect(() => {
      dispatch({
        type: NavTreeActionType.REPLACE_STATE,
        changedTree: headerNavTreeSecondary,
        isHeaderSecondary: true,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navTreeSecondaryJsonString]);

    /**
     * Restart measurement on `isSmallScreen` toogle
     */
    useEffect(() => {
      dispatch({
        type: NavTreeActionType.CLEAR_AND_START_MEASUREMENT,
      });
    }, [state.headerMetaData?.isSmallScreen]);

    const isNavTree = state.navTree.length;
    const isNavTreeSecondary = state.navTreeSecondary.length;
    const hasNavItems = isNavTree || isNavTreeSecondary;

    /**
     * Check visibility of primary nav items on container width or item width change
     */
    useEffect(() => {
      if (menuContainerWidth && isNavTree) {
        const secondNavWidth =
          isNavTreeSecondary &&
          panelSecondaryMenuRef.current?.getBoundingClientRect().width;

        const moreNavWidth = 60;
        let totalItemsWidth = 0;

        totalItemsWidth += moreNavWidth;
        if (secondNavWidth) {
          totalItemsWidth += secondNavWidth;
        }

        dispatch({
          type: NavTreeActionType.SET_ITEMS_VISIBILITY,
          totalItemsWidth,
          menuContainerWidth,
        });
      }
    }, [
      isNavTree,
      isNavTreeSecondary,
      menuContainerWidth,
      state.navItemsWidth,
    ]);

    const renderUserBlock = (): ReactElement => {
      return <div className="spark-header__sign-in">{userBlock}</div>;
    };

    const renderPrimaryMenu = (): ReactElement => (
      <PanelMenu
        ref={panelMenuRef}
        _privateProps={{ isHeader: true, moreNavAriaLabel }}
      />
    );

    const renderSecondaryMenu = (): ReactElement => (
      <PanelMenu
        ref={panelSecondaryMenuRef}
        _privateProps={{ isHeader: true, isHeaderSecondary: true }}
      />
    );

    const value = {
      ...state,
      addActive: (itemId: string, isHeaderSecondary?: boolean): void => {
        dispatch({
          type: NavTreeActionType.ADD_ACTIVE,
          itemId,
          isHeaderSecondary,
        });
      },
      toggleExpand: (itemId: string, isHeaderSecondary?: boolean): void => {
        dispatch({
          type: NavTreeActionType.TOGGLE_EXPAND,
          itemId,
          isHeaderSecondary,
        });
      },
    };

    const onKeyDownHandler: React.KeyboardEventHandler<HTMLDivElement> = (
      event
    ): void => {
      if (isHamburgerMenuOpen && event.key === "Escape") {
        setIsHamburgerMenuOpen(false);
      }
    };

    const logo = useMemo(() => {
      if (noLogo) return null;

      return (
        customLogo || (
          <span className="spark-header__logo">
            <i className="spark-logo spark-logo--sabre spark-logo--sm">Sabre</i>
          </span>
        )
      );
    }, [customLogo, noLogo]);

    return (
      <NavContext.Provider value={{ state: value, dispatch }}>
        <header
          ref={headerNode}
          className={headerClasses}
          data-breakpoint={state.headerMetaData?.breakpoint}
        >
          <nav className={navClasses} aria-label={navAriaLabel}>
            {hasNavItems ? (
              <button
                type="button"
                className="spark-menu__toggle spark-header__toggle"
                aria-label="View Navigation"
                title="View Navigation"
                onClick={(): void =>
                  setIsHamburgerMenuOpen(!isHamburgerMenuOpen)
                }
                ref={menuOpenNode}
              >
                <i className="spark-icon--fill spark-icon-menu-hamburger spark-icon--md"></i>
              </button>
            ) : null}
            {logo}
            <a
              {...titleProps}
              href={titleProps.href ?? titleLink}
              className={classNames(
                "spark-header__title",
                titleProps.className
              )}
            >
              {title}
            </a>
            {hasNavItems ? (
              <FocusTrap enabled={isHamburgerMenuOpen}>
                <div
                  ref={menuContainerRef as React.LegacyRef<HTMLDivElement>}
                  className="spark-menu spark-header__menu"
                  role="menu"
                  onKeyDown={onKeyDownHandler}
                  tabIndex={-1}
                >
                  <div className="spark-menu__header">
                    <button
                      type="button"
                      className="spark-menu__toggle"
                      aria-label="Hide Navigation"
                      title="Hide Navigation"
                      onClick={(): void =>
                        setIsHamburgerMenuOpen(!isHamburgerMenuOpen)
                      }
                      ref={menuCloseNode}
                    >
                      <i className="spark-icon--fill spark-icon-close"></i>
                    </button>
                    <span className="spark-menu__title"></span>
                  </div>
                  {isNavTree ? renderPrimaryMenu() : null}
                  {isNavTreeSecondary ? renderSecondaryMenu() : null}
                </div>
              </FocusTrap>
            ) : null}
            {userBlock ? renderUserBlock() : null}
          </nav>
          {childrenCategory.other}
          <HeaderCssOverrides />
          {isNavTreeSecondary ? <HeaderSecondaryNavCssOverrides /> : null}
        </header>
      </NavContext.Provider>
    );
  }
);

Header.displayName = "Header";

type HeaderSet = typeof Header & {
  Nav: typeof Navigation;
  NavItem: typeof NavigationItem;
};

const HeaderSetObject = Object.assign(Header, {
  Nav: Navigation,
  NavItem: NavigationItem,
}) as HeaderSet;

export { HeaderSetObject };
